import { bestPhone } from './bestPhone';
import { computeLeadScore } from './scoring/computeLeadScore';
import { LEAD_SCORE_CONFIGS } from './scoring/configs';

import type { ContactabilityInput, SignalContext } from './scoring/types';
import type {
  PersonReport,
  PhoneReport,
  PropertyReport,
  serializedTypes,
} from '@ltvco/refresh-lib/v1';
import type { LeadScoreResultData, LeadScoreUseCase } from 'api/leadScore';

export interface BuildLeadScoreResultParams {
  useCase: LeadScoreUseCase;
  address: string;
  propertyReport: PropertyReport;
  subjectProperty: serializedTypes.SerializedProperty;
  personReport: PersonReport | null;
  phoneReport: PhoneReport | null;
  propertyPermalink: string;
  personPermalink?: string;
  phonePermalink?: string;
  fallbackOwnerName?: string;
}

export const buildLeadScoreResult = ({
  useCase,
  address,
  propertyReport,
  subjectProperty,
  personReport,
  phoneReport,
  propertyPermalink,
  personPermalink,
  phonePermalink,
  fallbackOwnerName,
}: BuildLeadScoreResultParams): LeadScoreResultData => {
  const owner = personReport?.getOwner({
    potentialOwnerIndex: 0,
    higherConfidenceThreshold: 0,
    showHighConfidenceDataOnly: false,
  });
  const identity = personReport?.getIdentity(0);
  const personAddresses = personReport?.data.people?.[0]?.contact?.addresses;

  const signalContext: SignalContext = {
    subjectProperty,
    reportListings: propertyReport.data.listings,
    identity,
    personAddresses,
    personCourts: owner?.courts,
    now: new Date(),
  };

  const supportingPhone = phoneReport?.supportingPhone;
  const contactability: ContactabilityInput = {
    isDoNotCall: supportingPhone?.risk?.is_do_not_call ?? false,
    isActive: supportingPhone?.connectivity?.is_active ?? false,
    userActivity: supportingPhone?.connectivity?.user_activity,
    hasEmail: !!owner?.emails?.[0]?.address,
    hasPhoneReport: !!phoneReport,
  };

  const config = LEAD_SCORE_CONFIGS['motivated-seller'];
  const engineResult = computeLeadScore(config, signalContext, contactability);

  const lastSoldYear = subjectProperty.last_sold_date?.parsed?.year;
  const lastSoldSummary =
    lastSoldYear != null ? `Last sold in ${lastSoldYear}` : undefined;

  const contactName = owner?.ownerName || fallbackOwnerName;
  const contactPhone = bestPhone(owner?.phones)?.number ?? undefined;
  const contactEmail = owner?.emails?.[0]?.address ?? undefined;
  const hasContactInfo = !!(contactName || contactPhone || contactEmail);

  const primaryContact = hasContactInfo
    ? {
        name: contactName || '',
        phone: contactPhone,
        email: contactEmail,
        dnc: phoneReport ? contactability.isDoNotCall : null,
      }
    : undefined;

  return {
    config: config.configId,
    useCase,
    score: engineResult.score,
    maxScore: 100,
    tier: engineResult.tier,
    action: engineResult.action,
    dnc: primaryContact?.dnc ?? null,
    headline: engineResult.headline,
    description: engineResult.description,
    address,
    primaryContact,
    reasons: engineResult.reasons,
    contacts: primaryContact ? [primaryContact] : [],
    signals: engineResult.signals,
    lastSoldSummary,
    contactability,
    propertyReportPermalink: propertyPermalink,
    personReportPermalink: personPermalink,
    phoneReportPermalink: phonePermalink,
  };
};
